import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ListChecks, Loader2, Plus, Trash2, ChevronRight } from 'lucide-react'
import { useBot } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { botsApi } from '@/shared/api/bots'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import Toast from '@/components/ui/Toast'
import type { Resource } from '@/shared/types'

export default function BotWhitelist() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hapticFeedback, hapticNotification, webApp } = useTelegram()
  const { data: bot, isLoading: botLoading } = useBot(botId)

  // Fetch whitelisted resources (now returns full resource objects)
  const { data: whitelistData, isLoading: whitelistLoading } = useQuery({
    queryKey: ['bot-whitelisted-resources', botId],
    queryFn: () => botsApi.getWhitelistedResources(botId!),
    enabled: !!botId,
  })

  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showToast, setShowToast] = useState(false)

  // Auto-manage back button
  useBackButton()

  // Get resources directly from the API response
  const whitelistedResources = whitelistData?.resources || []

  // Remove resource mutation
  const removeResourceMutation = useMutation({
    mutationFn: async (resourceId: string) => {
      if (!botId || !whitelistData) throw new Error('Bot or whitelist data is required')
      const updatedList = whitelistData.resource_ids.filter(id => id !== resourceId)
      await botsApi.setWhitelistedResources(botId, updatedList)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot-whitelisted-resources', botId] })
      queryClient.invalidateQueries({ queryKey: ['bot', botId] })
      queryClient.invalidateQueries({ queryKey: ['bots'] })
      // Invalidate resource-side queries for bidirectional sync
      queryClient.invalidateQueries({ queryKey: ['resource-whitelisted-bots'] })
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      setToastMessage('Ресурс успешно удален из списка')
      setToastType('success')
      setShowToast(true)
      hapticNotification('success')
    },
    onError: (error: any) => {
      const message = error.response?.data?.details || error.message || 'Не удалось удалить ресурс'
      setToastMessage(message)
      setToastType('error')
      setShowToast(true)
      hapticNotification('error')
    },
  })

  const handleRemoveResource = (resource: Resource) => {
    webApp?.showPopup({
      title: 'Удалить ресурс',
      message: `Вы уверены, что хотите удалить "${resource.name}" из белого списка?`,
      buttons: [
        { id: 'cancel', type: 'cancel' },
        { id: 'confirm', type: 'destructive', text: 'Удалить' },
      ],
    }, (buttonId) => {
      if (buttonId === 'confirm') {
        hapticFeedback('medium')
        removeResourceMutation.mutate(resource.id)
      }
    })
  }

  // Helper to get logo URL
  const getLogoUrl = (resource: Resource) => {
    if (!resource.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/resources/${resource.id}/logo`
  }

  // Resource type metadata
  const typeMetadata = {
    channel: { label: 'Канал', icon: '📢', color: '#3b82f6' },
    bot: { label: 'Бот', icon: '🤖', color: '#8b5cf6' },
    group: { label: 'Группа', icon: '👥', color: '#10b981' },
    boost: { label: 'Буст', icon: '🚀', color: '#f59e0b' }
  }

  if (botLoading || whitelistLoading || !bot) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
              <ListChecks className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-tg-text">Мои ресурсы</h1>
              <p className="text-sm text-tg-hint mt-1">{bot.name}</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-tg-text">{whitelistedResources.length}</div>
              <div className="text-xs text-tg-hint">ресурсов</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Info Card */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
          <p className="text-sm text-blue-400">
            Ваши ресурсы для продвижения через этого бота. Оплата не требуется.
          </p>
        </div>

        {/* Add Resource Button */}
        <button
          onClick={() => {
            hapticFeedback('light')
            window.scrollTo(0, 0)
            navigate(`/bots/${botId}/whitelist/add`)
          }}
          className="w-full bg-tg-button text-tg-button-text rounded-xl p-4 hover:opacity-90 active:opacity-75 transition-opacity flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Добавить ресурс</div>
              <div className="text-sm opacity-90">Выберите ресурсы для добавления</div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 opacity-75" />
        </button>

        {/* Whitelisted Resources Section */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                <ListChecks className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Добавленные ресурсы ({whitelistedResources.length})</h2>
                <p className="text-xs text-tg-hint">Ресурсы для продвижения через бота</p>
              </div>
            </div>
          </div>

          {whitelistedResources.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-tg-hint font-medium">Пока нет добавленных ресурсов</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {whitelistedResources.map((resource) => {
                const logoUrl = getLogoUrl(resource)
                const typeMeta = typeMetadata[resource.resource_type]

                return (
                  <div
                    key={resource.id}
                    onClick={() => {
                      hapticFeedback('light')
                      navigate(`/traffic/${resource.id}`, {
                        state: { returnTo: `/bots/${botId}/whitelist` }
                      })
                    }}
                    className="p-4 bg-tg-bg rounded-2xl shadow-sm hover:shadow-md active:scale-[0.98] transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center">
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={resource.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const parent = target.parentElement
                              if (parent && typeMeta) {
                                parent.innerHTML = `<div class="text-2xl">${typeMeta.icon}</div>`
                              }
                            }}
                          />
                        ) : (
                          <div className="text-2xl">{typeMeta?.icon || '📦'}</div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-tg-text truncate">
                          {resource.name}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-tg-hint">
                          {resource.username && (
                            <span className="truncate">@{resource.username}</span>
                          )}
                          {typeMeta && (
                            <span>{typeMeta.label}</span>
                          )}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveResource(resource)
                        }}
                        disabled={removeResourceMutation.isPending}
                        className="p-2 text-red-600 hover:bg-red-500/10 rounded-lg transition-colors active:scale-95 disabled:opacity-50 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  )
}
